import type { ReactNode } from 'react'

export type GameHeaderProps = {
  title: string
  brandSrc: string
  lobbyLabel: string
  onLobby: () => void
  balance: number
  balanceLabel?: string
  connected?: boolean
  connectedLabel?: string
  disconnectedLabel?: string
  unknownConnectionLabel?: string
  meta?: string
  onOpenPlayers?: () => void
  playersLabel?: string
  onOpenRules?: () => void
  rulesLabel?: string
  onOpenHistory?: () => void
  historyLabel?: string
  extraActions?: ReactNode
}

function formatBalance(value: number): string {
  return Math.round(value).toLocaleString('en-US')
}

function ChevronLeft() {
  return (
    <svg className="header-chevron" viewBox="0 0 16 16" width="18" height="18" aria-hidden>
      <path
        d="M10.2 2.6 4.8 8l5.4 5.4"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function CoinMark() {
  return (
    <svg className="header-coin" viewBox="0 0 20 20" width="15" height="15" aria-hidden>
      <circle cx="10" cy="10" r="8.2" fill="#e8b84a" />
      <circle cx="10" cy="10" r="6.1" fill="#f6d36a" />
      <path
        d="M10.1 5.4c-1.9 0-3.2 1-3.2 2.4 0 1.2.8 1.9 2.5 2.3l.9.2c1 .2 1.4.5 1.4 1.1 0 .7-.7 1.2-1.7 1.2-1.1 0-1.8-.5-2-.9"
        fill="none"
        stroke="#8a5a12"
        strokeWidth="1.3"
        strokeLinecap="round"
      />
      <path d="M10 5.2v9.6" fill="none" stroke="#8a5a12" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  )
}

function InfoIcon() {
  return (
    <svg viewBox="0 0 20 20" width="16" height="16" fill="none" aria-hidden>
      <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="1.6" />
      <path d="M10 9v5M10 6.2v.6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  )
}

function HistoryIcon() {
  return (
    <svg viewBox="0 0 20 20" width="16" height="16" fill="none" aria-hidden>
      <path
        d="M3.5 10a6.5 6.5 0 1 1 2 4.6L3 17"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M10 6v4.2l2.6 1.6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  )
}

function UsersIcon() {
  return (
    <svg viewBox="0 0 20 20" width="16" height="16" fill="none" aria-hidden>
      <path
        d="M6.5 7.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5ZM13.5 8a2 2 0 1 0 0-4 2 2 0 0 0 0 4ZM2.5 16.5v-.8a4.2 4.2 0 0 1 4.2-4.2h1.6a4.2 4.2 0 0 1 4.2 4.2v.8M13.5 11.5a3.5 3.5 0 0 1 3.5 3.5v1.5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

export function GameHeader({
  title,
  brandSrc,
  lobbyLabel,
  onLobby,
  balance,
  balanceLabel,
  connected,
  connectedLabel,
  disconnectedLabel,
  unknownConnectionLabel,
  meta,
  onOpenPlayers,
  playersLabel = 'Players',
  onOpenRules,
  rulesLabel,
  onOpenHistory,
  historyLabel,
  extraActions,
}: GameHeaderProps) {
  const isChinese = typeof document !== 'undefined' && document.documentElement.lang.startsWith('zh')
  const resolvedUnknownConnectionLabel =
    unknownConnectionLabel || (isChinese ? '連線狀態未知' : 'Connection status unavailable')
  const resolvedRulesLabel = rulesLabel || (isChinese ? '遊戲規則' : 'Rules')
  const resolvedHistoryLabel = historyLabel || (isChinese ? '遊戲紀錄' : 'History')
  const statusText =
    connected === true
      ? connectedLabel
      : connected === false
        ? disconnectedLabel
        : resolvedUnknownConnectionLabel
  const statusTitle = [statusText, meta].filter(Boolean).join(' · ')
  const statusClass =
    connected === true ? ' on' : connected === false ? ' off' : ' unknown'

  return (
    <header className="header game-header">
      <div className="header-zone-left">
        <button
          type="button"
          className="btn-lobby"
          onClick={onLobby}
          aria-label={lobbyLabel}
          title={lobbyLabel}
        >
          <ChevronLeft />
        </button>
      </div>

      <div className="header-brand">
        <img className="game-badge" src={brandSrc} width={26} height={26} alt="" />
        <h1>{title}</h1>
        <span
          className={`status-dot${statusClass}`}
          role="status"
          title={statusTitle}
          aria-label={statusTitle}
        />
      </div>

      <div className="header-zone-right">
        <div className="header-asset" aria-label={balanceLabel || formatBalance(balance)}>
          <CoinMark />
          <strong>{formatBalance(balance)}</strong>
        </div>

        {onOpenPlayers && (
          <button
            type="button"
            className="header-action-btn"
            onClick={onOpenPlayers}
            aria-label={playersLabel}
            title={playersLabel}
          >
            <UsersIcon />
          </button>
        )}

        {onOpenHistory && (
          <button
            type="button"
            className="header-action-btn"
            onClick={onOpenHistory}
            aria-label={resolvedHistoryLabel}
            title={resolvedHistoryLabel}
          >
            <HistoryIcon />
          </button>
        )}

        {onOpenRules && (
          <button
            type="button"
            className="header-action-btn"
            onClick={onOpenRules}
            aria-label={resolvedRulesLabel}
            title={resolvedRulesLabel}
          >
            <InfoIcon />
          </button>
        )}

        {import.meta.env.VITE_DEMO_MODE === 'true' ? (
          <span
            role="status"
            title="Demo"
            style={{
              fontSize: '0.62rem',
              fontWeight: 800,
              letterSpacing: '0.08em',
              color: '#f6d36a',
              padding: '2px 6px',
              border: '1px solid rgba(246,211,106,0.45)',
              borderRadius: 6,
            }}
          >
            DEMO
          </span>
        ) : null}
        {extraActions}
      </div>
    </header>
  )
}
